import React from 'react';
import { Chip, CircularProgress } from '@mui/material';
import theme from 'app/theme';
import { useTranslation } from 'react-i18next';
import { LabelKey, StatusKey } from 'util/workflowStatus';
import { WAITING_STEPS, WaitingOnTooltip } from 'app/pages/overview/tabs/transactions/WaitingOnTooltip';

type Treatment = {
  bg: string;
  color: string;
  border?: string;
  spinner?: boolean;
};

// Figma's status pipeline: informational for New, warning for the pending states
// (lighter while it waits on a reviewer, stronger once it waits on an approver),
// success for workflow-complete, neutral for terminal. Text only — no icons.
const treatmentFor = (status: LabelKey, isCardholder: boolean): Treatment => {
  const pending = {
    bg: theme.palette.orange300.main,
    color: theme.palette.orange700.main,
  };
  const complete = {
    bg: theme.palette.green50.main,
    color: theme.palette.green700.main,
  };
  const terminal = {
    bg: theme.palette.highlight.main,
    color: theme.palette.neutral50.main,
    border: theme.palette.link400.main,
  };

  switch (status) {
    case 'new':
      return { bg: theme.palette.surfaceContainerHigh.main, color: theme.palette.primary.dark };
    case 'needsReview':
      return { bg: theme.palette.orange100.main, color: theme.palette.orange700.main };
    // Group steps share the pending treatment: from a scanning user's point of
    // view they mean the same thing — someone still has to approve this.
    case 'groupApproval':
    case 'groupApproval1':
    case 'groupFinalApproval':
    case 'pendingApproval':
    case 'pendingApproval1':
    case 'pendingFinalApproval':
      return pending;
    case 'approved':
    case 'reviewed':
      return complete;
    case 'posted':
    case 'bypassed':
    // Flex's terminal outcome reads like connected's: the workflow is over and the
    // transaction has left Speedchain for the customer's own books.
    case 'exported':
      return terminal;
    case 'posting':
      // The spinner is progress, not decoration, so it stays.
      return { bg: theme.palette.highlight.main, color: theme.palette.neutral50.main, spinner: true };
    case 'sageImportError':
      // SEE-1861: still posting, but Sage refused hh2's import — an alert, not progress.
      return { bg: theme.palette.red50.main, color: theme.palette.red700.main };
    case 'returned':
      // The cardholder is the one who has to act, so it reads as an alert for them
      // and as information for everyone else.
      return isCardholder
        ? { bg: theme.palette.red50.main, color: theme.palette.red700.main }
        : { bg: theme.palette.grey[200], color: theme.palette.grey[700] };
  }
};

export type TransactionStatusChipProps = {
  status?: StatusKey | null;
  isCardholder?: boolean;
  /** What the chip says, and what it takes its colour from. See labelKeyFor. */
  labelKey?: LabelKey;
  /** Set to show who the transaction is waiting on when the chip is hovered. */
  waitingOnTransactionId?: string;
};

export const TransactionStatusChip: React.FC<TransactionStatusChipProps> = ({
  status,
  isCardholder = false,
  labelKey,
  waitingOnTransactionId,
}) => {
  const { t } = useTranslation();
  if (!status) return null;

  const key = labelKey ?? status;
  const { bg, color, border, spinner } = treatmentFor(key, isCardholder);

  const chip = (
    <Chip
      label={t(`transactionsPage.tableRows.${key}`)}
      icon={spinner ? <CircularProgress size={16} style={{ color }} /> : undefined}
      sx={{
        color,
        backgroundColor: bg,
        fontWeight: 700,
        ...(border ? { border: `1px dashed ${border}` } : {}),
      }}
    />
  );

  if (!waitingOnTransactionId || !WAITING_STEPS.has(status)) return chip;
  return (
    <WaitingOnTooltip transactionId={waitingOnTransactionId} status={status}>
      {chip}
    </WaitingOnTooltip>
  );
};
